/**
 * Device geolocation helpers — Uber-style "use my location".
 *
 * Privacy rules (Kenya DPA / GDPR): coordinates are captured ONLY when the
 * user clicks the button (which triggers the browser's own permission
 * prompt), never silently on page load. Works on localhost and HTTPS;
 * plain-HTTP LAN deployments will get a permission error from the browser.
 */

/** Get the device position once. Resolves {lat, lng, accuracy} or throws. */
export function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Location is not supported by this browser.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: +pos.coords.latitude.toFixed(7),
        lng: +pos.coords.longitude.toFixed(7),
        accuracy: pos.coords.accuracy,
      }),
      (err) => {
        const msg = err.code === 1
          ? 'Location permission was denied. You can type the address instead.'
          : 'Could not get your location. You can type the address instead.';
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  });
}

/**
 * Best-effort reverse geocoding via OpenStreetMap Nominatim (no API key).
 * Returns a short human address, or null on any failure — callers always
 * keep the raw coordinates, so this is purely cosmetic.
 *
 * We compose the address from the structured fields rather than using
 * `display_name`: display_name concatenates the FULL admin hierarchy
 * ("South C, South C ward, Langata, Nairobi, Zimmerman, Nairobi County,
 * 00603, Kenya") which repeats itself and can include stray sibling areas.
 */
export async function reverseGeocode(lat, lng) {
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1`,
      { headers: { Accept: 'application/json' } },
    );
    if (!r.ok) return null;
    const a = (await r.json())?.address;
    if (!a) return null;
    const parts = [
      [a.house_number, a.road].filter(Boolean).join(' '),     // "123 Muhoho Ave"
      a.neighbourhood || a.suburb || a.quarter || a.village,  // "South C"
      a.city || a.town || a.municipality || a.county,         // "Nairobi"
    ].filter(Boolean);
    // de-dupe adjacent repeats ("South C, South C ward" style noise)
    const seen = new Set();
    const clean = parts.filter(p => {
      const k = p.toLowerCase().replace(/\s*ward$/, '');
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    return clean.length ? clean.join(', ') : null;
  } catch {
    return null;
  }
}

/**
 * Human description of a fix's accuracy radius. Desktop browsers have no
 * GPS chip — they geolocate via Wi-Fi/IP, which can be hundreds of meters
 * (or worse) off. Phones with GPS are typically <20 m.
 */
export const accuracyLabel = (m) => {
  if (m == null) return null;
  if (m <= 50) return null;                       // good fix — say nothing
  if (m <= 500) return `accurate to ~${Math.round(m)} m`;
  return `approximate (±${(m / 1000).toFixed(1)} km) — please confirm the address`;
};

/** "−1.2921, 36.8219" — coordinate fallback when reverse geocoding fails. */
export const fmtCoords = (lat, lng) => `${lat}, ${lng}`;
